import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { HiringHeroMetricResource } from 'util/resources/HiringHeroMetric';
import { useFeatureFlag } from 'hooks/redux/useFeatureFlag';

export interface MetricsData {
  receivedApplicants: HiringHeroMetricResource;
  applicationsMarked: HiringHeroMetricResource;
  readMessages: HiringHeroMetricResource;
}

const METRIC_CRITERIA_IS_PERCENTAGE = {
  receive_applicants: false,
  applications_marked: true,
  read_messages: true,
};

export const useHiringHeroMetric = (): {
  enabled: boolean;
  loaded: boolean;
  isHiringHero: boolean;
  metrics?: MetricsData;
  avgProgress?: number;
} => {
  const enabled = useFeatureFlag('hiring_hero');
  const metricsArr = useSelector(
    (state) => state['employer'].hiringHeroMetrics as HiringHeroMetricResource[],
  );
  const loaded = Boolean(metricsArr?.length);

  const isHiringHero = loaded && metricsArr.every((metric) => metric.met);
  const metrics = useMemo(
    () =>
      loaded
        ? {
            receivedApplicants: metricsArr.find(
              (metric) => metric.metricType === 'receive_applicants',
            ),
            applicationsMarked: metricsArr.find(
              (metric) => metric.metricType === 'applications_marked',
            ),
            readMessages: metricsArr.find((metric) => metric.metricType === 'read_messages'),
          }
        : null,
    [metricsArr],
  );

  const avgProgress = useMemo(() => {
    const currentProgress = metricsArr
      .filter((metric) => METRIC_CRITERIA_IS_PERCENTAGE.hasOwnProperty(metric.metricType))
      .reduce(
        (acc, currMetric) =>
          METRIC_CRITERIA_IS_PERCENTAGE[currMetric.metricType]
            ? (Math.min(currMetric.criteria, currMetric.percentage) / currMetric.criteria) * 100 +
              acc
            : currMetric.percentage + acc,
        0,
      );

    return Math.floor(currentProgress / Object.values(METRIC_CRITERIA_IS_PERCENTAGE).length);
  }, [metricsArr]);

  return {
    enabled,
    isHiringHero,
    loaded,
    metrics,
    avgProgress,
  };
};
